/**
 * PersistentLobeChat - Keeps LobeChat iframe mounted across navigation
 *
 * Mirrors PersistentGhost pattern:
 * - Mounted when user first visits /ai (lazy initialization)
 * - Kept mounted when navigating away (hidden with CSS)
 * - Shown instantly when returning to /ai
 *
 * Simpler than Chatwoot/Ghost — no URL sync needed (LobeChat is a
 * single-page chat interface, no deep linking required).
 */

import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import LobeChatEmbed from "@/components/LobeChatEmbed/LobeChatEmbed";
import type { LobeChatEmbedRef } from "@/components/LobeChatEmbed/LobeChatEmbed";
import { useAuthStore } from "@/lib/store/authStore";

export function PersistentLobeChat() {
  const location = useLocation();
  const isOnAiPage = location.pathname.startsWith("/ai");

  // Lazy mount: only render after first visit
  const [hasVisitedAi, setHasVisitedAi] = useState(false);
  const [mountKey, setMountKey] = useState(0);
  const lobeChatRef = useRef<LobeChatEmbedRef>(null);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  // Reset on logout
  const wasAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      setHasVisitedAi(false);
      setMountKey((prev) => prev + 1);
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Lazy mount on first /ai visit
  useEffect(() => {
    if (isOnAiPage && !hasVisitedAi) {
      setHasVisitedAi(true);
    }
  }, [isOnAiPage, hasVisitedAi]);

  const handleReady = useCallback(() => {
    // LobeChat loaded
  }, []);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentLobeChat] Error:", message);
  }, []);

  // Don't render if never visited or not provisioned
  if (!hasVisitedAi || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnAiPage ? "visible" : "hidden",
        pointerEvents: isOnAiPage ? "auto" : "none",
      }}
    >
      <LobeChatEmbed
        ref={lobeChatRef}
        key={mountKey}
        className="h-full w-full"
        onReady={handleReady}
        onError={handleError}
      />
    </div>
  );
}
